import * as React from 'react';
import { createContext, useContext, useEffect, useMemo, useState } from 'react';

import { generateElementId } from 'bundles/author-common/utils/IdUtils';
import { useLearningPathActivitiesMutation } from 'bundles/enterprise-admin-learning-path/hooks';
import { getActivities } from 'bundles/enterprise-admin-learning-path/utils';
import { stringifyProductId } from 'bundles/enterprise-admin-program-catalog/utils/collection';
import {
  extractTextFromDescriptionCMLString,
  wrapTextWithCMLString,
} from 'bundles/enterprise-legacy-learner-home/utils/programCommonUtils';
import type { InjectedNaptime } from 'bundles/naptimejs';
// @ts-expect-error TS7016 Untyped import http://go.dkandu.me/strict-ts-migration#TS7016
import ProgramCurriculumDomainsV1 from 'bundles/naptimejs/resources/programCurriculumDomains.v1';
import ProgramCurriculumsV1 from 'bundles/naptimejs/resources/programCurriculums.v1';
import type { ClipItem, CurriculumItem, Track } from 'bundles/naptimejs/resources/programCurriculums.v1';

import _t from 'i18n!nls/enterprise-admin-program-catalog';

const TRACK_TYPES = { CORE_TRACKS: 'coreTracks', ELECTIVE_TRACKS: 'electiveTracks' } as const;

export type CurriculumContextProviderProps = {
  canMoveAndCopyProductsInCollection: (collectionId: string) => boolean;
  canDeleteProductsInCollection: (collectionId: string) => boolean;
  createNewCollection: (title: string, description: string) => void;
  copyItemsToOtherCollections: (currentCollectionId: string, collectionIds: string[], productIds: string[]) => void;
  deleteCollection: (collectionId: string) => void;
  deleteProductsFromCollection: (collectionId: string, productIds: string[]) => void;
  getCollection: (collectionId: string) => Track | undefined;
  getCollectionDescription: (collectionId: string) => string;
  getCollectionDisplayOrder: () => string[];
  getListOfCollectionNamesAndIds: () => { collectionId: string; collectionName: string; dynamic?: boolean }[];
  getNewestCertificateIDsCollection: (collectionId: string) => Track | undefined;
  getCourseAndS12nIdsFromCollection: (collection?: Track) => {
    s12nIds: string[];
    courseIds: string[];
    clipsItems: ClipItem[];
  };
  getAllS12nsInCurriculum: () => string[];
  getIsCollectionLocked: (collectionId: string) => boolean;
  moveItemsToOtherCollections: (currentCollectionId: string, collectionIds: string[], productIds: string[]) => void;
  updateCollectionDescriptionAndTitle: (collectionId: string, description: string, title: string) => void;
  updateCollectionDisplayOrder: (
    newIndex: number,
    oldIndex: number,
    options?: { careerAcademy?: boolean; learningPaths?: boolean }
  ) => void;
  updateProductOrderInsideACollection: (collectionId: string, newIndex: number, oldIndex: number) => void;
  updateCurriculumDomains: (domainIds: string[]) => void;
  canEditCollection: (collectionId: string) => boolean;
  isDefaultCollection: boolean;
  isSmallSubsetCurriculum: boolean;
  refreshCurriculum: () => Promise<void>;
};

export const CurriculumContext = createContext<CurriculumContextProviderProps>({
  createNewCollection: () => undefined,
  canMoveAndCopyProductsInCollection: () => false,
  canDeleteProductsInCollection: () => false,
  copyItemsToOtherCollections: () => undefined,
  deleteCollection: () => undefined,
  deleteProductsFromCollection: () => undefined,
  getCollection: () => undefined,
  getCollectionDescription: () => '',
  getCollectionDisplayOrder: () => [],
  getListOfCollectionNamesAndIds: () => [],
  getNewestCertificateIDsCollection: () => undefined,
  getCourseAndS12nIdsFromCollection: () => {
    return { s12nIds: [], courseIds: [], clipsItems: [] };
  },
  getIsCollectionLocked: () => false,
  getAllS12nsInCurriculum: () => [],
  moveItemsToOtherCollections: () => undefined,
  updateCollectionDescriptionAndTitle: () => undefined,
  updateCollectionDisplayOrder: () => undefined,
  updateProductOrderInsideACollection: () => undefined,
  updateCurriculumDomains: () => undefined,
  canEditCollection: () => false,
  isDefaultCollection: false,
  isSmallSubsetCurriculum: false,
  refreshCurriculum: () => Promise.resolve(),
});

export const useCurriculumDataAndFunctions = () => {
  return useContext(CurriculumContext);
};

export type Props = {
  curriculumId: string;
  programId?: string;
  curriculum: ProgramCurriculumsV1;
  naptime: InjectedNaptime;
};

const CurriculumContextProvider: React.LegacyFunctionComponentWithChildren<Props> = ({
  children,
  curriculum,
  curriculumId,
  programId,
  naptime,
}) => {
  const DEFAULT_TITLE = _t('Default Collection');
  const [updateLearningPathActivities] = useLearningPathActivitiesMutation();
  const [shouldUpdateCurriculumContext, setShouldUpdateCurriculumContext] = useState<boolean>(false);
  const [collections, setCollections] = useState<{ [id: string]: Track }>({});
  const [collectionOrder, setCollectionOrder] = useState<string[]>([]);
  const [activeTrackType, setActiveTrackType] = useState<
    typeof TRACK_TYPES.CORE_TRACKS | typeof TRACK_TYPES.ELECTIVE_TRACKS | undefined
  >(undefined);

  const isDefaultCollection = Object.keys(collections).length === 1 && !Object.values(collections)[0].title;
  // Small subset curriculums contain only core tracks with no domain or subdomain options
  const isSmallSubsetCurriculum = useMemo(
    () =>
      Boolean(
        curriculum?.typeName === 'tracksCurriculum' &&
          !curriculum.definition?.coreTracks?.find((track) =>
            track.curriculumItems.find((item) => item.typeName === 'subdomainId' || item.typeName === 'domainId')
          )
      ),
    [curriculum?.typeName, curriculum?.definition?.coreTracks]
  );

  useEffect(() => {
    if (!curriculum) return;

    const currentTrackType = isSmallSubsetCurriculum ? TRACK_TYPES.CORE_TRACKS : TRACK_TYPES.ELECTIVE_TRACKS;
    setActiveTrackType(currentTrackType);

    const allCollectionsObject: { [id: string]: Track } = {};
    const trackOrder: string[] = [];

    curriculum.definition[currentTrackType].forEach((track) => {
      allCollectionsObject[track.id] = track;
      trackOrder.push(track.id);
    });

    setCollections(allCollectionsObject);
    setCollectionOrder(trackOrder);
  }, [curriculum, isSmallSubsetCurriculum, shouldUpdateCurriculumContext]);

  const updateCurriculum = async (
    newCollections: { [id: string]: Track } = collections,
    newCollectionOrder: string[] = collectionOrder,
    trackOverride?: typeof TRACK_TYPES.CORE_TRACKS | typeof TRACK_TYPES.ELECTIVE_TRACKS
  ) => {
    if (!activeTrackType) return;

    const newCurriculum = {
      id: curriculumId,
      typeName: curriculum.typeName,
      definition: {
        ...curriculum.definition,
        [trackOverride || activeTrackType]: newCollectionOrder.map((id) => newCollections[id]),
      },
      includeClips: true,
    };

    setCollections(newCollections);
    setCollectionOrder(newCollectionOrder);

    await naptime
      .executeMutation(
        ProgramCurriculumsV1.update(
          curriculumId + '?allowClearingClips=true&updateSource=CONTENT_CURATION',
          newCurriculum
        )
      )
      .then(() => naptime.refreshData({ resources: [ProgramCurriculumsV1.RESOURCE_NAME] }))
      .then(() => setShouldUpdateCurriculumContext((prevState) => !prevState));
  };

  const refreshCurriculum = async () => {
    await naptime
      .refreshData({ resources: [ProgramCurriculumsV1.RESOURCE_NAME] })
      .then(() => setShouldUpdateCurriculumContext((prevState) => !prevState));
  };

  const makeNewCml = (descriptionText: string) => {
    return {
      typeName: 'cml' as const,
      definition: { dtdId: '', value: wrapTextWithCMLString(descriptionText) },
    };
  };

  const createNewCollection = async (title: string, description: string) => {
    const id = generateElementId();
    const titleToUse = title || DEFAULT_TITLE;
    const newCollections = {
      ...collections,
      [id]: {
        title: titleToUse,
        description: makeNewCml(description),
        id,
        curriculumItems: [],
        allCurriculumItemIds: [],
      },
    };
    const newOrder = [...collectionOrder, id];
    await updateCurriculum(newCollections, newOrder);
  };

  const copyItemsToOtherCollections = async (
    currentCollectionId: string,
    collectionIds: string[],
    productIds: string[]
  ) => {
    const copiedItems = collections[currentCollectionId].curriculumItems.filter((item) =>
      productIds.includes(stringifyProductId(item))
    );
    const newCollections = { ...collections };
    collectionIds.forEach(
      // eslint-disable-next-line no-return-assign
      (id) => (newCollections[id].curriculumItems = newCollections[id].curriculumItems.concat(copiedItems))
    );
    await updateCurriculum(newCollections);
  };

  const deleteCollection = async (collectionId: string) => {
    if (!activeTrackType) return;
    const newCollections = { ...collections };
    delete newCollections[collectionId];
    const newCollectionOrder = collectionOrder.filter((id) => id !== collectionId);
    await updateCurriculum(newCollections, newCollectionOrder);
  };

  const getCollection = (collectionId: string) => {
    return collections[collectionId];
  };

  const getCollectionDescription = (collectionId: string) => {
    return extractTextFromDescriptionCMLString(collections[collectionId]?.description?.definition?.value) ?? '';
  };

  const getIsCollectionLocked = (collectionId: string) => {
    return Boolean(collections[collectionId]?.isLocked);
  };

  const canEditCollection = (collectionId: string) => {
    return !collections[collectionId]?.badgeTemplateId && !getIsCollectionLocked(collectionId);
  };

  const canMoveAndCopyProductsInCollection = (collectionId: string) => {
    return !(getIsCollectionLocked(collectionId) || collections[collectionId]?.badgeTemplateId) && !isDefaultCollection;
  };

  const canDeleteProductsInCollection = (collectionId: string) => {
    return !(getIsCollectionLocked(collectionId) || collections[collectionId]?.badgeTemplateId);
  };

  const deleteProductsFromCollection = async (collectionId: string, productIds: string[]) => {
    const newCollections = { ...collections };
    const currentCollection = newCollections[collectionId];
    if (!currentCollection) return;

    currentCollection.curriculumItems = currentCollection.curriculumItems.filter(
      (item) => !productIds.includes(stringifyProductId(item))
    );

    await updateCurriculum(newCollections);
  };

  const getCollectionDisplayOrder = () => {
    return collectionOrder;
  };

  const getCourseAndS12nIdsFromCollection = (collection?: Track) => {
    const s12nIds: string[] = [];
    const courseIds: string[] = [];
    const clipsItems: ClipItem[] = [];
    collection?.curriculumItems?.forEach((item) => {
      if (item.typeName === 'courseId') courseIds.push(stringifyProductId(item));
      if (item.typeName === 's12nId') s12nIds.push(stringifyProductId(item));
      if (item.typeName === 'clipId') clipsItems.push(item);
    });

    return { s12nIds, courseIds, clipsItems };
  };

  const getListOfCollectionNamesAndIds = () => {
    return Object.values(collections).map((collection) => {
      return {
        collectionId: collection.id,
        collectionName: collection.title || DEFAULT_TITLE,
        dynamic: collection.referenceCollectionConfiguration?.enableSync,
      };
    });
  };

  const getAllS12nsInCurriculum = () => {
    return curriculum?.definition?.allS12nIds ?? [];
  };

  const getNewestCertificateIDsCollection = (collectionId: string) => {
    const allS12nIds = getAllS12nsInCurriculum();
    const hasCoreTracks = Boolean(curriculum?.definition.coreTracks?.length);
    // Rare edge case where a program has both elective and core tracks
    const hasBothElectiveAndCoreTracks = hasCoreTracks && curriculum?.definition.electiveTracks?.length;

    if (hasBothElectiveAndCoreTracks || !hasCoreTracks || !curriculum.definition.allS12nIds) {
      return getCollection(collectionId);
    } else {
      const curriculumItems = collections[collectionId].curriculumItems.filter(
        (item) => item.typeName !== 'domainId' && item.typeName !== 'subdomainId'
      );

      const currentCollection = curriculum?.definition.coreTracks[0];
      if (!currentCollection) return undefined;

      return {
        id: currentCollection.id,
        title: currentCollection.title,
        description: makeNewCml(''),
        curriculumItems,
        allCurriculumItemIds: allS12nIds,
      } as Track;
    }
  };

  const moveItemsToOtherCollections = async (
    currentCollectionId: string,
    collectionIds: string[],
    productIds: string[]
  ) => {
    const productsToMove: CurriculumItem[] = [];
    const filteredCurrentCollection: CurriculumItem[] = [];

    collections[currentCollectionId].curriculumItems.forEach((item) => {
      if (productIds.includes(stringifyProductId(item))) {
        productsToMove.push(item);
      } else {
        filteredCurrentCollection.push(item);
      }
    });

    const newCollections = { ...collections };
    newCollections[currentCollectionId].curriculumItems = filteredCurrentCollection;
    if (newCollections[currentCollectionId].referenceCollectionConfiguration) {
      newCollections[currentCollectionId].referenceCollectionConfiguration.enableSync = false;
    }
    collectionIds.forEach(
      // eslint-disable-next-line no-return-assign
      (id) => (newCollections[id].curriculumItems = newCollections[id].curriculumItems.concat(productsToMove))
    );

    await updateCurriculum(newCollections);
  };

  const updateCollectionDescriptionAndTitle = async (collectionId: string, description: string, title: string) => {
    const newCollections = { ...collections };

    if (newCollections[collectionId].description) {
      newCollections[collectionId].description.definition.value = wrapTextWithCMLString(description);
    } else {
      newCollections[collectionId].description = {
        typeName: 'cml' as const,
        definition: { dtdId: '', value: wrapTextWithCMLString(description) },
      };
    }

    newCollections[collectionId].title = title;
    await updateCurriculum(newCollections);
  };

  const moveItemWithinAnArray = (array: Array<$TSFixMe>, newIndex: number, oldIndex: number) => {
    return array.splice(newIndex < 0 ? array.length + newIndex : newIndex, 0, array.splice(oldIndex, 1)[0]);
  };

  const updateCollectionDisplayOrder = async (
    newIndex: number,
    oldIndex: number,
    { careerAcademy, learningPaths }: { careerAcademy?: boolean; learningPaths?: boolean } = {}
  ) => {
    const displayedCollections = collectionOrder.filter((_) => {
      if (careerAcademy) {
        return collections[_].isLocked;
      }
      if (learningPaths) {
        return collections[_].badgeTemplateId;
      }
      return !collections[_].isLocked && !collections[_].badgeTemplateId;
    });
    const hiddenCollections = collectionOrder.filter((_) => !displayedCollections.includes(_));
    moveItemWithinAnArray(displayedCollections, newIndex, oldIndex);
    const newOrder = [...displayedCollections, ...hiddenCollections];
    await updateCurriculum(collections, newOrder);
  };

  const updateProductOrderInsideACollection = async (collectionId: string, newIndex: number, oldIndex: number) => {
    const newCollection = { ...collections[collectionId] };
    const curriculumItems = newCollection.curriculumItems;
    moveItemWithinAnArray(curriculumItems, newIndex, oldIndex);
    const newCollections = { ...collections, [collectionId]: newCollection };
    if (newCollection.badgeTemplateId && programId) {
      await updateLearningPathActivities({
        variables: {
          input: {
            badgeTemplateId: newCollection.badgeTemplateId,
            programId,
            activities: getActivities(newCollection.curriculumItems),
          },
        },
      });
    } else {
      await updateCurriculum(newCollections);
    }
  };

  // DomainIds are always added to the first item in coreTracks
  const updateCurriculumDomains = async (domainIds: string[]) => {
    const firstCoreTrackId = curriculum.definition.coreTracks?.[0]?.id || generateElementId();
    const newDomainItems = domainIds.map((id) => ({ typeName: 'domainId' as const, id, definition: { id } }));
    let newFirstCoreTrack: Track;

    if (curriculum.definition.coreTracks?.[0]?.id) {
      newFirstCoreTrack = { ...curriculum.definition.coreTracks[0] };
      newFirstCoreTrack.curriculumItems = [
        ...newFirstCoreTrack.curriculumItems.filter((item) => item.typeName !== 'domainId'),
        ...newDomainItems,
      ];
    } else {
      newFirstCoreTrack = {
        curriculumItems: newDomainItems,
        id: firstCoreTrackId,
        title: '',
        description: makeNewCml(''),
        allCurriculumItemIds: [],
      };
    }
    const isCoreTrack = activeTrackType === TRACK_TYPES.CORE_TRACKS;

    await updateCurriculum(
      { ...(isCoreTrack ? collections : {}), [firstCoreTrackId]: newFirstCoreTrack },
      isCoreTrack ? collectionOrder : [firstCoreTrackId],
      TRACK_TYPES.CORE_TRACKS
    ).then(() => naptime.refreshData({ resources: [ProgramCurriculumDomainsV1.RESOURCE_NAME] }));
  };

  return (
    <CurriculumContext.Provider
      value={{
        createNewCollection,
        canEditCollection,
        canDeleteProductsInCollection,
        canMoveAndCopyProductsInCollection,
        copyItemsToOtherCollections,
        deleteCollection,
        getCollection,
        getCollectionDescription,
        getCollectionDisplayOrder,
        deleteProductsFromCollection,
        getListOfCollectionNamesAndIds,
        getNewestCertificateIDsCollection,
        getCourseAndS12nIdsFromCollection,
        getIsCollectionLocked,
        getAllS12nsInCurriculum,
        moveItemsToOtherCollections,
        updateCollectionDescriptionAndTitle,
        updateCollectionDisplayOrder,
        updateProductOrderInsideACollection,
        updateCurriculumDomains,
        isDefaultCollection,
        isSmallSubsetCurriculum,
        refreshCurriculum,
      }}
    >
      {children}
    </CurriculumContext.Provider>
  );
};

export default CurriculumContextProvider;
